/**
 * <tss-product-tabs> — text tabs that swap a product grid in place (sections/product-tabs.liquid).
 *
 * The first tab is server-rendered (or, when the section sorts, fetched on load);
 * the rest load from `products-fetch`
 * and are cached per collection URL. The "View all" link follows the active tab.
 * Swiping the products sideways moves to the next / previous tab.
 *
 * Loaded from the section, i.e. before the importmap in scripts.liquid — so no
 * static `@theme/*` import (it would fail to resolve and kill the module). Motion
 * is imported lazily when a slide actually runs.
 */

const FETCH_SECTION = 'products-fetch';
const SWIPE_MIN_DISTANCE = 50; // px

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panel = this.querySelector('[role="tabpanel"]');
    this.grid = this.querySelector('[data-tabs-grid]');
    this.viewLink = this.querySelector('[data-tabs-view]');
    this.emptyMessage = this.querySelector('[data-tabs-empty]');
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.cache = new Map();
    this.abortController = null;

    this.loadingIndicator = this.querySelector('[data-tabs-loading]');
    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    // Sorted sections ship an empty grid; everything else caches the server-rendered first tab.
    if ('loadFirst' in this.dataset) {
      this.loadProducts(selectedTab?.dataset.url || '').finally(() => {
        if (this.loadingIndicator) this.loadingIndicator.hidden = true;
      });
    } else if (selectedTab?.dataset.url) {
      this.cache.set(
        selectedTab.dataset.url,
        [...this.grid.children].map((item) => item.cloneNode(true))
      );
    }

    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => this.onKeydown(event));

    if (this.tabs.length > 1) {
      this.panel.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
      this.panel.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
    }
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    this.touchStart = { x: touch.clientX, y: touch.clientY };
  }

  // A clearly sideways swipe only, so vertical page scrolling is never caught.
  onTouchEnd(event) {
    if (!this.touchStart) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStart.x;
    const deltaY = touch.clientY - this.touchStart.y;
    this.touchStart = null;
    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const step = deltaX < 0 !== isRtl ? 1 : -1;
    const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const nextTab = this.tabs[currentIndex + step];
    if (!nextTab) return;

    this.slideFrom = step;
    nextTab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    this.select(nextTab);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    const lastIndex = this.tabs.length - 1;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let targetIndex = null;

    if (event.key === (isRtl ? 'ArrowLeft' : 'ArrowRight')) targetIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    if (event.key === (isRtl ? 'ArrowRight' : 'ArrowLeft')) targetIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = lastIndex;
    if (targetIndex === null) return;

    event.preventDefault();
    this.tabs.forEach((tab, index) => (tab.tabIndex = index === targetIndex ? 0 : -1));
    this.tabs[targetIndex].focus();
  }

  async select(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.tabs.forEach((candidate) => {
      const isSelected = candidate === tab;
      candidate.setAttribute('aria-selected', String(isSelected));
      candidate.tabIndex = isSelected ? 0 : -1;
    });
    this.panel.setAttribute('aria-labelledby', tab.id);
    this.updateGaList(tab);
    if (this.viewLink && tab.dataset.url) this.viewLink.href = tab.dataset.url;

    await this.loadProducts(tab.dataset.url);
  }

  // Same naming as the Liquid for the first tab: "Heading: Tab", id "heading-tab".
  updateGaList(tab) {
    const heading = this.dataset.gaHeading || 'Products';
    const label = tab.textContent.trim();
    const slug = (text) =>
      text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-|-$/g, '');
    this.panel.dataset.gaList = JSON.stringify({ name: `${heading}: ${label}`, id: `${slug(heading)}-${slug(label)}` });
  }

  async loadProducts(collectionUrl) {
    this.abortController?.abort();

    if (!collectionUrl) return this.renderItems([]);
    if (this.cache.has(collectionUrl)) return this.renderItems(this.cache.get(collectionUrl));

    this.abortController = new AbortController();
    this.panel.setAttribute('aria-busy', 'true');

    try {
      // Tab URLs may already carry a query (?sort_by=…), so add section_id properly.
      const fetchUrl = new URL(collectionUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', FETCH_SECTION);
      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = [...html.querySelectorAll('[data-products-fetch-grid] > li')].slice(0, this.limit);
      this.cache.set(collectionUrl, items);
      this.renderItems(items);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[product-tabs] fetch failed:', error);
      this.renderItems([]);
    } finally {
      this.panel.removeAttribute('aria-busy');
    }
  }

  renderItems(items) {
    this.grid.replaceChildren(...items.map((item) => item.cloneNode(true)));
    this.slideIn();
    if (this.emptyMessage) this.emptyMessage.hidden = items.length > 0;
  }

  // Swipe-driven switches slide the new products in from the swipe side.
  async slideIn() {
    const direction = this.slideFrom;
    this.slideFrom = 0;
    if (!direction || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      const { animate } = await import('@theme/motion');
      animate(this.grid, { opacity: [0, 1], x: [direction * 40, 0] }, { duration: 0.3, ease: 'easeOut' });
    } catch {
      // No animation is fine; the products have already swapped.
    }
  }
}

if (!customElements.get('tss-product-tabs')) {
  customElements.define('tss-product-tabs', ProductTabs);
}
